/**
 * `/kerdoiv/{token}` — the post-event survey of handover rev7 §11.10.
 *
 * "Post-event survey (U13, E + 1 bd 10:00), five optional questions: how many
 * came · how the quantity was (*sok maradt · kicsit maradt · épp elég volt ·
 * hamar elfogyott · kevés volt*) · what went first · what was left most ·
 * would you order again."
 *
 * **Every question is optional, and the page says so once rather than five
 * times.** A customer who sends the form with nothing in it has answered: that
 * is the response rate §11.10 shows beside every verdict. There is no required
 * field and no validation that stops them.
 *
 * The page carries the order's reference and date so the customer knows which
 * event they are answering about, and nothing else off the order — no price,
 * no quantity, no production figure (§5.11, `docs/overnight_constraints.md` §1
 * and §3). What it may show is decided in `public.survey_public()`, not here.
 *
 * Hungarian only, magázó, as every customer surface in release one is (§5.12).
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'

import { readPublicConfig } from '../lib/env.ts'
import { readSurvey, submitSurvey, type SurveyView } from '../lib/survey.ts'
import { formatter } from '../page/format.ts'

const f = formatter('hu')

export function SurveyPage(): JSX.Element {
  const { token = '' } = useParams()
  const config = useMemo(() => readPublicConfig(), [])

  const [survey, setSurvey] = useState<SurveyView | null>(null)
  const [missing, setMissing] = useState(false)
  const [sent, setSent] = useState(false)
  const [sending, setSending] = useState(false)
  const [failed, setFailed] = useState(false)

  const [guestsCame, setGuestsCame] = useState('')
  const [verdict, setVerdict] = useState('')
  const [wentFirst, setWentFirst] = useState('')
  const [leftMost, setLeftMost] = useState('')
  const [again, setAgain] = useState('')

  useEffect(() => {
    let cancelled = false
    void readSurvey(config, token).then((answer) => {
      if (cancelled) return
      if (answer.ok !== true || answer.survey === undefined) {
        setMissing(true)
        return
      }
      setSurvey(answer.survey)
      /* The answers already recorded, seeded into any field the person has
         not already typed into.
         **`setX(previous => …)` and not `setX(value)`**: the form is drawn
         before this fetch returns, so somebody can be typing while it is in
         flight — and a plain set then wipes what they typed the moment the
         answer lands. The browser suite caught it as an intermittent
         `guests_came: null` where 38 had been filled in (run 19, and again on
         20 October 2026); on a slower machine it is a customer losing a
         headcount they had just entered. A field the person has touched is
         theirs. */
      const given = answer.survey.answers ?? {}
      const seed = (value: string) => (typed: string): string => (typed === '' ? value : typed)
      setGuestsCame(seed(given.guests_came === null || given.guests_came === undefined
        ? ''
        : String(given.guests_came)))
      setVerdict(seed(given.quantity_verdict ?? ''))
      setWentFirst(seed(given.went_first ?? ''))
      setLeftMost(seed(given.left_most ?? ''))
      setAgain(seed(given.would_order_again === null || given.would_order_again === undefined
        ? ''
        : (given.would_order_again ? 'igen' : 'nem')))
    })
    return () => {
      cancelled = true
    }
  }, [config, token])

  const send = useCallback((): void => {
    setSending(true)
    setFailed(false)
    void submitSurvey(config, token, {
      guests_came: guestsCame.trim() === '' ? null : Number(guestsCame),
      quantity_verdict: verdict === '' ? null : verdict,
      went_first: wentFirst.trim() === '' ? null : wentFirst.trim(),
      left_most: leftMost.trim() === '' ? null : leftMost.trim(),
      would_order_again: again === '' ? null : again === 'igen',
    })
      .then((answer) => {
        setSending(false)
        if (answer.ok === true) setSent(true)
        else setFailed(true)
      })
      .catch(() => {
        setSending(false)
        setFailed(true)
      })
  }, [config, token, guestsCame, verdict, wentFirst, leftMost, again])

  if (missing) {
    return (
      <main className="payshell" data-testid="survey-page" data-state="missing">
        <h1>Ezt a kérdőívet nem találjuk.</h1>
        <div className="note stop">
          Kérjük, ellenőrizze a linket a levelünkben. Ha nem sikerül, válaszoljon erre a levélre, és
          segítünk.
        </div>
      </main>
    )
  }

  if (sent) {
    return (
      <main className="payshell" data-testid="survey-page" data-state="sent">
        <h1>Köszönjük.</h1>
        <div className="note ok" data-testid="survey-thanks">
          A válaszát rögzítettük. Sokat segít abban, hogy legközelebb pontosabban becsüljük a
          mennyiséget.
        </div>
        {survey?.revobox_out !== null && survey?.revobox_out !== undefined &&
          survey.revobox_out > 0 && (
          <div className="note">
            A kiszállított {f.n(survey.revobox_out)} RevoBox dobozért kollégánk jelentkezik.
          </div>
        )}
      </main>
    )
  }

  return (
    <main className="payshell" data-testid="survey-page" data-state="form">
      <h1>Hogy sikerült?</h1>

      {survey?.ref === undefined ? null : (
        <div className="card">
          <div className="sub">Rendelésazonosító</div>
          <div className="ref">{survey.ref}</div>
          <div className="sub" data-testid="survey-event">
            {survey.event_name === null || survey.event_name === undefined
              ? ''
              : `${survey.event_name} · `}
            {survey.event_date === undefined
              ? ''
              : f.date(new Date(`${survey.event_date}T00:00:00`), true)}
          </div>
        </div>
      )}

      <div className="note" data-testid="survey-optional">
        Öt rövid kérdés. <strong>Mindegyik kihagyható</strong> — amelyikre nem szeretne válaszolni,
        hagyja üresen.
      </div>

      <label className="f" htmlFor="survey-guests">Hányan voltak végül?</label>
      <input
        id="survey-guests"
        type="number"
        min={0}
        inputMode="numeric"
        value={guestsCame}
        onChange={(event) => setGuestsCame(event.target.value)}
        data-testid="survey-guests"
      />

      <label className="f" htmlFor="survey-verdict">Milyen volt a mennyiség?</label>
      <div className="acts" role="group" aria-labelledby="survey-verdict" data-testid="survey-verdicts">
        {(survey?.verdicts ?? []).map((step) => (
          <button
            key={step.key}
            type="button"
            className="chip"
            aria-pressed={verdict === step.key}
            onClick={() => setVerdict(verdict === step.key ? '' : step.key)}
            data-testid={`survey-verdict-${step.key}`}
          >
            {step.label}
          </button>
        ))}
      </div>

      <label className="f" htmlFor="survey-first">Mi fogyott el elsőként?</label>
      <input
        id="survey-first"
        type="text"
        value={wentFirst}
        onChange={(event) => setWentFirst(event.target.value)}
        data-testid="survey-first"
      />

      <label className="f" htmlFor="survey-left">Miből maradt a legtöbb?</label>
      <input
        id="survey-left"
        type="text"
        value={leftMost}
        onChange={(event) => setLeftMost(event.target.value)}
        data-testid="survey-left"
      />

      <label className="f" htmlFor="survey-again">Rendelne tőlünk újra?</label>
      <select
        id="survey-again"
        value={again}
        onChange={(event) => setAgain(event.target.value)}
        data-testid="survey-again"
      >
        <option value="">—</option>
        <option value="igen">Igen</option>
        <option value="nem">Nem</option>
      </select>

      <div className="acts">
        <button
          type="button"
          className="cta"
          disabled={sending}
          onClick={send}
          data-testid="survey-submit"
        >
          Válasz elküldése
        </button>
      </div>

      {failed && (
        <div className="note stop" data-testid="survey-failed">
          A választ most nem tudtuk rögzíteni. Kérjük, próbálja meg néhány perc múlva.
        </div>
      )}

      {survey?.revobox_out !== null && survey?.revobox_out !== undefined && survey.revobox_out > 0 && (
        <div className="note" data-testid="survey-revobox">
          A kiszállított {f.n(survey.revobox_out)} RevoBox dobozért kollégánk jelentkezik.
        </div>
      )}
    </main>
  )
}
